import { useState, useEffect, useMemo, useCallback } from 'react';

import { ReactComponent as AddIcon } from '../../../icons/add.svg';

import { getClientColumnDefinition, getTabsByKey } from './client.schema';
import './clients.scss';

import { useClients } from '../../../api/hooks/useClients';
import { ACTIVE_COMPANY_TYPES } from '../../../utils/mock';
import { ErrorDialog } from '../../common/dialog/error-dialog/ErrorDialog';
import { YesNoDialog } from '../../common/dialog/yes-no-dialog/YesNoDialog';
import { TabItem, TabLayout } from '../../common/tab-layout/TabLayout';
import { Table } from '../../common/table/Table';
import { FormModal } from '../../form/modal/FormModal';

export const Clients = ({ defaultType = ACTIVE_COMPANY_TYPES[0].value }) => {
  const {
    clients,
    loading,
    error,
    processing,
    createClient,
    updateClient,
    removeClient,
    getClientById,
    resetClient
  } = useClients();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [display, setDisplay] = useState(false);
  const [currentType, setCurrentType] = useState(defaultType);
  const [activeTab, setActiveTab] = useState(0);

  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogText, setErrorDialogText] = useState('Unknown Error');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogText, setDeleteDialogText] = useState('');
  const [deleteId, setDeleteId] = useState();

  const [activationDialogOpen, setActivationDialogOpen] = useState(false);
  const [activationDialogText, setActivationDialogText] = useState('');
  const [activationValue, setActivationValue] = useState(false);
  const [activationData, setActivationData] = useState();

  const [formData, setFormData] = useState({ type: defaultType });

  useEffect(() => {
    if (!open) {
      setCurrentType(defaultType);
      setFormData({ type: defaultType });
    }
  }, [open, defaultType]);

  const tabs = useMemo(() => getTabsByKey(currentType), [currentType]);

  const handleEditClick = async (item, displayMode = false) => {
    resetClient();
    const client = clients.find((c) => c.id === item.id);
    if (!client) return;
    setOpen(true);

    await getClientById(client.id, (result) => {
      const clientTypeValue = typeof result.type === 'object' ? result.type.value : result.type;
      const newType = ACTIVE_COMPANY_TYPES.find((t) => t.value === clientTypeValue).value;

      if (newType) {
        setCurrentType(newType);
      } else {
        setCurrentType(defaultType);
      }

      setFormData({ ...result, type: newType ?? result.type });
      setEditId(item.id);
      setDisplay(displayMode);
    });
  };

  const showErrorDialog = (message) => {
    setErrorDialogText(message);
    setErrorDialogOpen(true);
  };

  const handleViewClick = async (item) => {
    resetClient();
    await handleEditClick(item, true);
  };

  const handleCreateClick = () => {
    resetClient();
    setEditId(null);
    setCurrentType(defaultType);
    setFormData({ type: defaultType });
    setOpen(true);
    setDisplay(false);
  };

  const handleSave = (data) => {
    createClient({
      data,
      onSuccess: () => setOpen(false),
      onError: () => showErrorDialog('There was an error creating the client. Please try again!')
    });
  };

  const handleUpdate = (id, data) => {
    updateClient({
      data,
      onSuccess: () => setOpen(false),
      onError: () => showErrorDialog('There was an error updating the client. Please try again!')
    });
  };

  const showDeleteDialog = (client) => {
    setDeleteDialogText(
      `Are you sure you want to delete client '${client.name}' with id '${client.clientId}'?`
    );
    setDeleteId(client.id);
    setDeleteDialogOpen(true);
  };

  const showActivationDialog = (client, value) => {
    setActivationDialogText(
      `Are you sure you want to ${value ? 'activate' : 'deactivate'} client '${
        client.name
      }' with id '${client.clientId}'?`
    );
    setActivationData(client);
    setActivationValue(value);
    setActivationDialogOpen(true);
  };

  const handleOnDelete = () => {
    removeClient({
      data: deleteId,
      onSuccess: () => setDeleteDialogOpen(false),
      onComplete: () => {
        setDeleteDialogOpen(false);
        setDeleteId(null);
      },
      onError: () => showErrorDialog('There was an error deleting the client. Please try again!')
    });
  };

  const handleOnActivate = () => {
    updateClient({
      data: { ...activationData, active: true },
      onSuccess: () => setActivationDialogOpen(false),
      onComplete: () => setActivationDialogOpen(false),
      onError: () => showErrorDialog('There was an error activating the client. Please try again!')
    });
  };

  const handleOnDeactivate = () => {
    updateClient({
      data: { ...activationData, active: false },
      onSuccess: () => setActivationDialogOpen(false),
      onComplete: () => setActivationDialogOpen(false),
      onError: () =>
        showErrorDialog('There was an error deactivating the client. Please try again!')
    });
  };

  const handleFormModalChange = useCallback(
    (newFormData) => {
      setFormData(newFormData);

      if (newFormData.type !== currentType) {
        const newType =
          typeof newFormData.type === 'object' ? newFormData.type?.value : newFormData.type;
        if (newType) {
          setCurrentType(newType);
        }
      }
    },
    [currentType]
  );

  const filters = [
    {
      key: 'type',
      label: 'Type',
      options: ACTIVE_COMPANY_TYPES
    }
  ];

  return (
    <>
      <FormModal
        isOpen={open && !errorDialogOpen}
        onClose={() => {
          setOpen(false);
          setEditId(null);
          setFormData({ type: defaultType });
        }}
        display={display}
        title={
          editId
            ? display
              ? 'View Client'
              : 'Edit Client'
            : processing
              ? 'Client'
              : 'Create Client'
        }
        isEdit={!!editId}
        itemId={editId}
        tabs={tabs}
        formData={formData}
        onFormChange={handleFormModalChange}
        saveItem={handleSave}
        updateItem={handleUpdate}
        loading={processing}
      />

      <ErrorDialog
        isOpen={errorDialogOpen}
        message={errorDialogText}
        onClose={() => setErrorDialogOpen(false)}
        onClick={() => {
          resetClient();
          setErrorDialogOpen(false);
        }}
      />

      <YesNoDialog
        title="Delete Client"
        isOpen={deleteDialogOpen}
        message={deleteDialogText}
        onYesClick={handleOnDelete}
        onNoClick={() => setDeleteDialogOpen(false)}
        onClose={() => setDeleteDialogOpen(false)}
        loading={processing}
      />

      <YesNoDialog
        title={activationValue ? 'Activate Client' : 'Deactivate Client'}
        isOpen={activationDialogOpen}
        message={activationDialogText}
        onYesClick={activationValue ? handleOnActivate : handleOnDeactivate}
        onNoClick={() => setDeleteDialogOpen(false)}
        onClose={() => setDeleteDialogOpen(false)}
        loading={processing}
      />

      <TabLayout activeIndex={activeTab} onChange={setActiveTab}>
        <TabItem title="Active">
          <div className="clients__tab-content">
            <Table
              id="active-clients"
              columns={getClientColumnDefinition(
                false,
                handleEditClick,
                handleViewClick,
                showDeleteDialog,
                (data) => showActivationDialog(data, true),
                (data) => showActivationDialog(data, false)
              )}
              data={clients?.filter((client) => client.active) ?? []}
              filters={filters}
              action="Create"
              actionIcon={AddIcon}
              onActionClick={handleCreateClick}
              loading={loading}
              error={error}
            />
          </div>
        </TabItem>
        <TabItem title="Inactive">
          <div className="clients__tab-content">
            <Table
              id="inactive-clients"
              columns={getClientColumnDefinition(
                true,
                handleEditClick,
                handleViewClick,
                showDeleteDialog,
                (data) => showActivationDialog(data, true),
                (data) => showActivationDialog(data, false)
              )}
              data={clients?.filter((client) => !client.active) ?? []}
              filters={filters}
              action="Create"
              actionIcon={AddIcon}
              onActionClick={handleCreateClick}
            />
          </div>
        </TabItem>
      </TabLayout>
    </>
  );
};
